import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import { Grid } from '@material-ui/core';
import { format } from 'date-fns';
import copy from 'copy-to-clipboard';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { allowBindUDRStates, policyType as PolicyType } from '@ourbranch/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { offerUsesAdvanceQuoteDays, stateHasAdvanceQuoteDays } from '../../helpers/advance-quote-helpers';
import { isPastOrWithin7Days, isEffectiveDateInPast } from '../../helpers/date-helpers';
import { useLazyQuery } from '@apollo/client';

import { useToast } from 'core/components/toast';
import { setTouchedFromErrors } from 'core/components/validation-errors-modal/validation-errors-modal';
import { useStore } from 'core/store/store.mobx';
import useSession from 'core/hooks/use-session';
import { offerHasValidOptions } from 'core/helpers/underwriting-form-helpers';
import { getMVRCheckStatus } from 'core/helpers/mvr-helpers';
import { getPolicyType } from 'core/helpers/policy-type';
import FillSkippedDataPulls from './components/fill-skipped-data-pulls';
import FEDOfferFooter from './components/fed-offer-footer';
import OfferExpired from './components/offer-expired';
import UpdateOffer from './components/update-offer';
import AQDOfferFooter from './components/aqd-past-effective-date';
import UWForm from './components/UW-form';
import Checkout from './components/checkout';
import FarmOfferFooter from './components/farm-offer-footer';
import InvalidFooter from './components/invalid-footer';
import { RECALCULATE_QUOTE_TO_CLUSTER } from 'core/store/offer-store/offer-queries';
import { types } from 'core/helpers/sanitize';
import BranchPlusOnlyFooter from './components/branch-plus-only-footer';
import alertDarkGreen from 'core/assets/svg/alert-dark-green.svg';
import alertWhite from 'core/assets/svg/alert-white-bold.svg';
import checkmark from 'core/assets/svg/check.svg';
import { track } from 'core/helpers/analytics';
import { buildProspectFunnelProperties, trackProspectFunnelEvent } from 'core/helpers/prospect-funnel';
import useStyles from './footer.styles';

// Stringify defensively: a MobX observable that didn't fully unwrap, or any
// other circular reference, would otherwise throw inside JSON.stringify and
// kill the diagnostic effect below.
const safeJsonStringify = (value) => {
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
};

const Footer = observer(({ showValidationErrorsModal, setShowValidationErrorsModal, hasDisabledAffinity }) => {
  const classes = useStyles();
  const history = useHistory();
  const toast = useToast();
  const { handleSubmit, dirty, values, initialValues, validateForm, setErrors, setTouched } = useFormikContext();

  // Diagnostic: when the form is dirty, log which fields differ between
  // initialValues and values. Helpful for tracking down "form stays dirty
  // after save" regressions where some field round-trip is asymmetric
  // (response missing a field, type coercion, embedded vs reference cache,
  // etc.). Open DevTools → Console to inspect. (#30634)
  //
  // Stringified content in the deps (not the object refs) so the effect
  // only fires when the values actually change — Formik creates new
  // object refs on every render, which would otherwise spam the console
  // and re-walk the offer tree on every hover / mobx observation tick
  // (devloai PR review on #30634).
  const dirtyValuesKey = dirty ? safeJsonStringify(values) : '';
  const dirtyInitialKey = dirty ? safeJsonStringify(initialValues) : '';
  useEffect(() => {
    if (!dirty) {
      return;
    }
    const diffs = [];
    // Depth guard prevents stack overflow on accidental circular refs
    // (e.g. a MobX observable that didn't fully serialize) and caps the
    // log size on the deeply nested farm offer tree.
    const MAX_DEPTH = 20;
    const MAX_DIFFS = 100;
    const findDiffs = (init, curr, path = '', depth = 0) => {
      if (diffs.length >= MAX_DIFFS) {
        return;
      }
      if (depth > MAX_DEPTH) {
        diffs.push({ path: path || '(root)', initial: '[max depth]', current: '[max depth]' });
        return;
      }
      if (init === curr) {
        return;
      }
      if (init == null || curr == null || typeof init !== 'object' || typeof curr !== 'object') {
        diffs.push({ path: path || '(root)', initial: init, current: curr });
        return;
      }
      const keys = new Set([...Object.keys(init), ...Object.keys(curr)]);
      for (const key of keys) {
        findDiffs(init[key], curr[key], path ? `${path}.${key}` : key, depth + 1);
      }
    };
    findDiffs(initialValues, values);
    if (diffs.length) {
      console.warn('[DIRTY FIELDS] Formik dirty=true. Mismatched fields:', diffs);
    } else {
      console.warn('[DIRTY FIELDS] Formik dirty=true but no diffs found (possible reference inequality)');
    }
  }, [dirty, dirtyValuesKey, dirtyInitialKey]);
  const {
    offer: store,
    account: { sendEventAsMember }
  } = useStore();
  const session = useSession();
  const { isAgency } = session;

  const { cars, drivers, global } = store.offer.quote;
  const { setShowUDRModal, isStale, isOutdated, state } = store;
  const optionsAvailable = offerHasValidOptions({
    offer: store?.offer,
    priorQuoteWithPreBindUWRejections: store.priorQuoteWithPreBindUWRejections
  });
  const offerId = store?.offer?.id;
  const quoteDetails = types.QuoteDetailsInput(store?.offer?.quote);

  const { hasMVRToRun, hasPostBindMVrs } = getMVRCheckStatus(store, dirty, values);
  const needToUpdateMVRs = hasMVRToRun || hasPostBindMVrs;
  const hasInvalidVins =
    values?.selectedOption?.includes(PolicyType.Auto) && (cars || []).some((car) => String(car.make).startsWith('*'));
  const isExpiredOffer = isStale && isOutdated; // more than 30 days
  const isOutOfDateOffer = isStale && !isOutdated; // between 1-29 days old
  const needToFillSkippedData =
    !isOutOfDateOffer && !hasInvalidVins && !dirty && store.offer && store.offer.skippedDataPulls?.length > 0;
  const hasUDR = drivers.some((driver) => driver.autoViolations?.UDR >= 1) && !allowBindUDRStates.includes(store.state);

  // Farm underwriting approval details
  const approvalDetails = store.offer?.quote?.preBindUWCheck?.approvalDetails;
  const sentToUnderwritingAt = approvalDetails?.sentToUnderwritingAt;
  const sentToUnderwritingBy = approvalDetails?.sentToUnderwritingBy;
  const underwriterApprovedAt = approvalDetails?.underwriterApprovedAt;
  const underwriterApprovedBy = approvalDetails?.underwriterApprovedBy;
  const formattedSentDate = sentToUnderwritingAt
    ? format(new Date(sentToUnderwritingAt), "MM/dd/yyyy '@' h:mm a 'EST'")
    : null;
  const formattedApprovedDate = underwriterApprovedAt
    ? format(new Date(underwriterApprovedAt), "MM/dd/yyyy '@' h:mm a 'EST'")
    : null;
  const farmOfferWaitingForApproval =
    isFarmBasedPolicyType(store.selectedOption) && sentToUnderwritingAt && !underwriterApprovedAt;
  const farmOfferIsApproved =
    isFarmBasedPolicyType(store.selectedOption) && sentToUnderwritingAt && underwriterApprovedAt;

  const [recalculateQuoteToCluster, { loading }] = useLazyQuery(RECALCULATE_QUOTE_TO_CLUSTER, {
    variables: { offerId, revisedQuoteDetails: quoteDetails }
  });

  const onCheckout = useCallback(async () => {
    const checkoutErrors = await validateForm();
    if (Object.keys(checkoutErrors).length > 0) {
      setErrors(checkoutErrors);
      setTouched(setTouchedFromErrors(checkoutErrors), false);
      setShowValidationErrorsModal(true);
      await sendEventAsMember({
        eventName: 'Checkout validation error - Applicant',
        userId: store?.offer?.webUserId,
        properties: JSON.stringify(
          buildProspectFunnelProperties({
            offer: store?.offer,
            session,
            extraProps: {
              selectedOption: values?.selectedOption,
              validationErrors: checkoutErrors
            }
          })
        )
      });
      track('Staff User: Checkout Validation Error', {
        offerId: store?.offer?.id,
        webUserId: store?.offer?.webUserId,
        agent: session?.user?.username,
        isAgency,
        selectedOption: values?.selectedOption,
        validationErrors: checkoutErrors
      });
    } else if (hasUDR) {
      setShowUDRModal(true);
    } else {
      await sendEventAsMember({
        eventName: 'Staff checkout viewed - Applicant',
        userId: store?.offer?.webUserId,
        properties: JSON.stringify(
          buildProspectFunnelProperties({
            offer: store?.offer,
            session,
            extraProps: {
              selectedOption: values?.selectedOption,
              policyTypes: values.selectedOption
            }
          })
        )
      });
      track('Staff User: Checkout Viewed', {
        offerId: store?.offer?.id,
        webUserId: store?.offer?.webUserId,
        agent: session?.user?.username,
        isAgency,
        selectedOption: values?.selectedOption
      });
      history.push(`/offer/${store.offer.id}/${values.selectedOption}/checkout`);
    }
  }, [
    history,
    setErrors,
    setTouched,
    store.offer,
    store.offer.id,
    store?.offer?.webUserId,
    values,
    isAgency,
    session,
    sendEventAsMember,
    setShowValidationErrorsModal,
    setShowUDRModal,
    validateForm,
    hasUDR
  ]);

  const onContinueToUWForm = useCallback(async () => {
    const updateErrors = await validateForm();
    if (Object.keys(updateErrors).length > 0) {
      setErrors(updateErrors);
      setTouched(setTouchedFromErrors(updateErrors), false);
      setShowValidationErrorsModal(true);
    } else if (hasUDR) {
      setShowUDRModal(true);
    } else {
      history.push(`/offer/${store.offer.id}/verifyUW`);
    }
  }, [
    history,
    setShowValidationErrorsModal,
    setShowUDRModal,
    validateForm,
    setErrors,
    setTouched,
    store.offer.id,
    hasUDR
  ]);

  const onUpdate = async () => {
    if (!needToFillSkippedData) {
      const updateErrors = await validateForm();
      const hasValidationErrors = Object.keys(updateErrors).length > 0;
      // The Update Offer footer renders in 3 visual variants (standard,
      // FED, AQD) that all converge here; fire one funnel event from this
      // single validation point so the validationErrors payload is accurate
      // and consistent across variants.
      await trackProspectFunnelEvent({
        sendEventAsMember,
        eventName: 'Staff Funnel: Update Offer',
        offer: store?.offer,
        session,
        extraProps: {
          selectedOption: store?.selectedOption,
          hasValidationErrors,
          validationErrors: updateErrors
        }
      });
      track('Staff User: Update Offer Clicked', {
        offerId: store?.offer?.id,
        webUserId: store?.offer?.webUserId,
        agent: session?.user?.username,
        isAgency,
        selectedOption: store?.selectedOption,
        hasValidationErrors,
        validationErrors: updateErrors
      });
      if (hasValidationErrors) {
        setErrors(updateErrors);
        setTouched(setTouchedFromErrors(updateErrors), false);
        setShowValidationErrorsModal(true);
      } else {
        handleSubmit();
      }
    }
  };

  const copyLink = async (readOnly) => {
    const { protocol, host } = window.location;
    const newHost = host.replace(/staff\./i, 'www.'); // staff.ourbranch.com -> www.ourbranch.com, for example

    const selectedOption = values.selectedOption;

    let policyType;
    if (selectedOption === 'HB' || selectedOption === 'AB') {
      // if we're giving a bundled price, link to the bundle
      policyType = 'HA';
    } else {
      policyType = selectedOption;
    }

    if (!readOnly) {
      const link = store.offer.clusterId
        ? `${protocol}//${newHost}/review?cid=${store.offer.clusterId}&planType=${store.offer.code}&policyType=${policyType}&internal=true`
        : `${protocol}//${newHost}/quote/detail/${store.offer.id}`;
      const copied = copy(link);
      if (copied) {
        toast.notify({
          type: 'success',
          message: 'The link to a customizable quote has been copied to your clipboard.'
        });
      } else {
        toast.notify({
          type: 'error',
          message: `We could not copy the link, but here it is: ${link}`
        });
      }
    } else {
      const checkoutErrors = await validateForm();

      let clusterId;
      if (store.offer.clusterId) {
        clusterId = store.offer.clusterId;
      } else {
        const { data } = await recalculateQuoteToCluster();
        clusterId = data.cluster.id;
      }

      const readOnlyLink = `${protocol}//${newHost}/offer-review?cid=${clusterId}&policyType=${policyType}`;
      const copiedLink = copy(readOnlyLink);

      if (Object.keys(checkoutErrors).length > 0) {
        setErrors(checkoutErrors);
        setTouched(setTouchedFromErrors(checkoutErrors), false);
        setShowValidationErrorsModal(true);
      } else if (hasUDR) {
        setShowUDRModal(true);
      } else if (copiedLink) {
        toast.notify({
          type: 'success',
          message: 'The link to a read-only quote has been copied to your clipboard.',
          durationInSeconds: 5
        });
      } else {
        toast.notify({
          type: 'error',
          message: `We could not copy the link, but here it is: ${readOnlyLink}`
        });
      }
    }
  };

  const { hasHome, hasCondo, isRenters, hasFarm, hasAuto } = getPolicyType(store.selectedOption);
  const { homeEffectiveDate, autoEffectiveDate, condoEffectiveDate, rentersEffectiveDate, farmEffectiveDate } =
    (values && values.global) || global;
  let dwellingEffectiveDate;
  let dwellingPolicyType;
  if (hasHome) {
    dwellingEffectiveDate = homeEffectiveDate;
    dwellingPolicyType = PolicyType.Home;
  } else if (hasCondo) {
    dwellingEffectiveDate = condoEffectiveDate;
    dwellingPolicyType = PolicyType.Condo;
  } else if (isRenters) {
    dwellingEffectiveDate = rentersEffectiveDate;
    dwellingPolicyType = PolicyType.Renters;
  } else if (hasFarm) {
    dwellingEffectiveDate = farmEffectiveDate;
    dwellingPolicyType = store.selectedOption;
  }

  // Auto effective-date checks must gate on hasAuto. The autoEffectiveDate
  // field on `global` is initialised when an offer is first created and
  // persists even when the customer's selectedOption is later switched to a
  // non-Auto product (e.g. Farm-only). For Farm-only offers the stale auto
  // date is typically several weeks behind the present, which used to fire
  // a false-positive "effective date(s) are in the past" footer. (#30633)
  const dwellingNeedsUpdate = dwellingEffectiveDate && isPastOrWithin7Days(dwellingEffectiveDate, state);
  const autoNeedsUpdate = hasAuto && autoEffectiveDate && isPastOrWithin7Days(autoEffectiveDate, state);
  const hasAdvanceQuotePolicy = offerUsesAdvanceQuoteDays(state, store.selectedOption);
  // If state is in AQD at all, FED footer should never show (even for H-only offers)
  const stateIsInAQD = stateHasAdvanceQuoteDays(state);

  const dwellingEffectiveDateInPast = dwellingEffectiveDate && isEffectiveDateInPast(dwellingEffectiveDate, state);
  const autoEffectiveDateInPast = hasAuto && autoEffectiveDate && isEffectiveDateInPast(autoEffectiveDate, state);
  const hasEffectiveDateInPast = dwellingEffectiveDateInPast || autoEffectiveDateInPast;

  //need to check if effective dates have been changed and the new date is in the past
  const effectiveDatesDirtyAndInPast =
    (!!hasHome &&
      !!values?.global?.homeEffectiveDate &&
      values.global.homeEffectiveDate !== global.homeEffectiveDate &&
      isEffectiveDateInPast(values.global.homeEffectiveDate, state)) ||
    (!!hasAuto &&
      !!values?.global?.autoEffectiveDate &&
      values.global.autoEffectiveDate !== global.autoEffectiveDate &&
      isEffectiveDateInPast(values.global.autoEffectiveDate, state)) ||
    (!!hasCondo &&
      !!values?.global?.condoEffectiveDate &&
      values.global.condoEffectiveDate !== global.condoEffectiveDate &&
      isEffectiveDateInPast(values.global.condoEffectiveDate, state)) ||
    (!!isRenters &&
      !!values?.global?.rentersEffectiveDate &&
      values.global.rentersEffectiveDate !== global.rentersEffectiveDate &&
      isEffectiveDateInPast(values.global.rentersEffectiveDate, state)) ||
    (!!hasFarm &&
      !!values?.global?.farmEffectiveDate &&
      values.global.farmEffectiveDate !== global.farmEffectiveDate &&
      isEffectiveDateInPast(values.global.farmEffectiveDate, state));

  const effectiveDateIsInPast = hasEffectiveDateInPast || effectiveDatesDirtyAndInPast;
  // All States shared rule (reference advance-quote-helpers.js)
  const shouldShowPastEffectiveDateFooter = isOutOfDateOffer && effectiveDateIsInPast;
  // AQD states: non-stale offer where user has set effective date in the past
  const shouldShowAQDFooter = !isExpiredOffer && hasAdvanceQuotePolicy && effectiveDateIsInPast && !isOutOfDateOffer;

  const [shouldShowCheckboxAnUpdateButton, setShouldShowCheckboxAnUpdateButton] = useState(
    dwellingNeedsUpdate || autoNeedsUpdate
  );
  const invalidOffer = hasDisabledAffinity || !optionsAvailable;

  return (
    <div id="offer_footer" className={classes.floatingContainer}>
      <div
        className={classNames(
          classes.footerContainer,
          {
            [classes.footerAlert]: isExpiredOffer || !optionsAvailable || hasDisabledAffinity
          },
          {
            [classes.extendedFooterContainer]:
              !invalidOffer &&
              isOutOfDateOffer &&
              shouldShowCheckboxAnUpdateButton &&
              !stateIsInAQD &&
              !(farmOfferIsApproved || farmOfferWaitingForApproval)
          },
          {
            [classes.footerContainerWithBanner]: farmOfferWaitingForApproval || farmOfferIsApproved
          }
        )}
      >
        <Grid container alignItems="center" className={classes.footerContent}>
          {hasDisabledAffinity ? (
            <InvalidFooter text="Sorry, you can't update the offer because your affinity code is disabled." />
          ) : (
            (store.isBranchPlusOnly && <BranchPlusOnlyFooter />) ||
            (isExpiredOffer && <OfferExpired />) ||
            // Out-of-date offer with past effective date, or AQD state with past effective date
            ((shouldShowPastEffectiveDateFooter || shouldShowAQDFooter) && <AQDOfferFooter onUpdate={onUpdate} />) ||
            (isOutOfDateOffer && !stateIsInAQD && (
              <FEDOfferFooter
                onUpdate={onUpdate}
                dwellingEffectiveDate={dwellingEffectiveDate}
                autoEffectiveDate={autoEffectiveDate}
                dwellingPolicyType={dwellingPolicyType}
                dwellingNeedsUpdate={dwellingNeedsUpdate}
                autoNeedsUpdate={autoNeedsUpdate}
                shouldShowCheckboxAnUpdateButton={shouldShowCheckboxAnUpdateButton}
                setShouldShowCheckboxAnUpdateButton={setShouldShowCheckboxAnUpdateButton}
              />
            )) ||
            (!optionsAvailable && <InvalidFooter text="There are no valid policy options for this offer." />) ||
            // All states: out-of-date offer falls back to standard update.
            // Surface both effects when stale AND dirty so the agent knows updating
            // will re-rate the offer and apply their edits; otherwise name whichever
            // single reason applies.
            ((dirty || isOutOfDateOffer) && (
              <UpdateOffer onUpdate={onUpdate} updateReason={isOutOfDateOffer ? (dirty ? 'both' : 'stale') : 'dirty'} />
            )) ||
            (isFarmBasedPolicyType(store.selectedOption) && (
              <FarmOfferFooter
                onContinueToCheckout={onCheckout}
                setShowValidationErrorsModal={setShowValidationErrorsModal}
              />
            )) ||
            (store.needsPreBindUWForm && (
              <UWForm showValidationErrorsModal={showValidationErrorsModal} onContinueToUWForm={onContinueToUWForm} />
            )) ||
            ((needToFillSkippedData || needToUpdateMVRs) && (
              <FillSkippedDataPulls copyLink={copyLink} setShowValidationErrorsModal={setShowValidationErrorsModal} />
            )) ||
            (!dirty && (
              <Checkout
                showValidationErrorsModal={showValidationErrorsModal}
                hasInvalidVins={hasInvalidVins}
                copyLink={copyLink}
                onCheckout={onCheckout}
                loading={loading}
              />
            ))
          )}
        </Grid>
        {farmOfferWaitingForApproval && (
          <div className={classes.sentToUnderwritingBanner}>
            <img src={alertDarkGreen} alt="" className={classes.bannerAlertIcon} />
            <span className={classes.sentToUnderwritingText}>
              This offer has been sent to underwriting on {formattedSentDate} by {sentToUnderwritingBy}.
              {!session.canApproveUWReview && ' You are not able to edit until underwriting has reviewed it.'}
            </span>
          </div>
        )}
        {farmOfferIsApproved && !dirty && (
          <div className={classes.approvedBanner}>
            <img src={checkmark} alt="" className={classes.bannerAlertIcon} />
            <span className={classes.sentToUnderwritingText}>
              This was approved for checkout by underwriting on {formattedApprovedDate} by {underwriterApprovedBy}. If
              you make changes to this offer, it may require another underwriting review.
            </span>
          </div>
        )}
        {farmOfferIsApproved && dirty && (
          <div className={classes.approvedDangerBanner}>
            <img src={alertWhite} alt="" className={classes.bannerAlertIcon} />
            <span className={classes.approvedDangerBannerText}>
              Changes made after approval. Once you're done updating, you may need to revisit the review page and
              resubmit to underwriting.
            </span>
          </div>
        )}
      </div>
    </div>
  );
});

Footer.propTypes = {
  showValidationErrorsModal: PropTypes.bool.isRequired,
  setShowValidationErrorsModal: PropTypes.func.isRequired
};

export default Footer;
